/**
 * i18n configuration for Life Journal.
 * Uses react-i18next with language detection and localStorage persistence.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Settings
      settings: "Settings",
      appearance: "Appearance",
      theme: "Theme",
      darkMode: "Dark mode",
      lightMode: "Light mode",
      language: "Language",
      navigation: "Navigation",
      dashboard: "Dashboard",
      openDashboard: "Open Dashboard",
      dashboardDesc: "Stats and overview for the month",
      insightsTrends: "Insights & Trends",
      insightsDesc: "View analytics and progress",
      account: "Account",
      signOut: "Sign out",
      installApp: "Download app",
      installAppDesc: "Install Life Journal on your device for quick access",
      installAppIOS: "Tap the Share button, then \"Add to Home Screen\"",
      quickAccess: "Quick access to your stats and overview.",
      journal: "Journal",
      name: "Name",
      favouriteMoments: "Favourite Moments",
      sketchGallery: "Sketch Gallery",
      sketchGallerySubtitle: "All your sketches in one place.",
      allJournalEntries: "All Journal Entries",
      allJournalEntriesSubtitle: "Browse and search all your journal entries.",
      allJournalEntriesCount: "{{shown}} of {{total}} entries",
      noJournalEntriesYet: "No journal entries yet.",
      noJournalEntriesHint: "Write in your journal to see entries here.",
      search: "Search",
      searchByDate: "Search by date...",
      searchSketches: "Search sketches",
      noSketchesYet: "No sketches yet.",
      noSketchesHint: "Sketch your day from the journal to see it here.",
      noMatchesFor: "No matches for \"{{query}}\"",
      sketchFrom: "Sketch from {{date}}",
      viewEntry: "View entry",
      sketchCount: "{{count}} of {{total}} sketches",
      units: "Units",
      weight: "Weight",
      totalXP: "Total XP",
      level: "Level",
      levelUp: "Level Up",
      editJournalSettings: "Edit Journal Settings",
      editJournalName: "Edit Journal Name",
      renameJournal: "Rename Journal",
      enterJournalName: "Enter journal name",
      journalNameCannotBeEmpty: "Journal name cannot be empty",
      characters: "characters",
      cancel: "Cancel",
      save: "Save",
      // Tabs
      journalTab: "Journal",
      journalAndHabits: "Journal and Habits",
      habitsTab: "Habits",
      trackersTab: "Trackers",
      friendsTab: "Friends",
      settingsTab: "Settings",
      // Common
      back: "Back",
      loading: "Loading...",
      // Auth
      verifyEmail: "Verify Your Email",
      verifySubtitle: "We sent a verification link to",
      verifyCheck: "Please check your inbox and click the link to continue.",
      verifiedBtn: "I've verified my email",
      signOutDifferent: "Sign out and use a different account",
      // Journal entry
      photo: "Photo",
      addPhoto: "Add a photo",
      addPhotoTypes: "Add a photo (JPEG, PNG, or WebP)",
      removePhoto: "Remove photo",
      sketchPad: "Sketch Pad",
      writeJournal: "Write Journal",
      tellStory: "Tell your story",
      sketchDay: "Sketch Day",
      doodleThoughts: "Doodle your thoughts",
      captureMoment: "Capture the moment",
      memorableMoments: "Memorable Moments",
      whatMadeSpecial: "What made this day special?",
      journalEntry: "Journal entry",
      yourThoughts: "Your thoughts...",
      pourThoughtsPlaceholder: "Pour your thoughts here...",
      addNewHabitPlaceholder: "Add a new habit...",
      todaysProgressLabel: "Today's Progress",
      memorableMoment: "Memorable moment...",
      // Dashboard
      statsFor: "Stats for {{month}} {{year}}",
      journalEntries: "Journal entries",
      ofDays: "of {{n}} days",
      memorableMomentsCount: "Memorable moments",
      habitsCompleted: "Habits completed",
      ofGoal: "{{pct}}% of goal",
      noHabitsSet: "No habits set",
      xpEarnedThisMonth: "XP earned this month",
      consistency: "Consistency",
      journalStreak: "Journal Streak",
      xpEarned: "XP Earned",
      moodAvg: "Mood Avg",
      levelInLevel: "Lv.{{level}} · {{current}}/{{total}} in level",
      last7DaysTrend: "Last 7 days trend",
      levelTotal: "Level {{level}} total",
      avgSleep: "Avg sleep",
      daysLogged: "{{n}} days logged",
      noData: "No data",
      logsCount: "{{n}} logs",
      moodDistribution: "Mood distribution",
      noMoodData: "No mood data for this month",
      noMoodSet: "no mood set",
      sleepOverMonth: "Sleep over the month (hours)",
      eightHourGoal: "— Green dashed line = 8h goal",
      noSleepData: "No sleep data for this month",
      weightTrend: "Weight trend ({{unit}})",
      habitCompletions: "Habit completions this month",
      habitCompletionsDesc: "Times each habit was completed in {{month}} {{year}}",
      moodBreakdown: "Mood breakdown",
      dayLabel: "Day {{n}}",
      moodTooltip: "Mood",
      mood: "Mood",
      sleepTooltip: "Sleep",
      weightTooltip: "Weight",
      moodCalendar: "Mood Calendar",
      tapToSet: "Tap to set",
      tapDayCycleMood: "Tap a day to cycle mood",
      noTrackerData: "No tracker data for this month yet. Start logging above!",
      sleepHoursLabel: "Sleep (hours)",
      saved: "Saved",
      uploading: "Uploading...",
      hours: "hours",
      hrs: "hrs",
      moodEcstatic: "Ecstatic",
      moodHappy: "Happy",
      moodNeutral: "Neutral",
      moodSad: "Sad",
      moodAngry: "Angry",
      monthJanuary: "January",
      monthFebruary: "February",
      monthMarch: "March",
      monthApril: "April",
      monthMay: "May",
      monthJune: "June",
      monthJuly: "July",
      monthAugust: "August",
      monthSeptember: "September",
      monthOctober: "October",
      monthNovember: "November",
      monthDecember: "December",
      monthShortJan: "Jan",
      monthShortFeb: "Feb",
      monthShortMar: "Mar",
      monthShortApr: "Apr",
      monthShortMay: "May",
      monthShortJun: "Jun",
      monthShortJul: "Jul",
      monthShortAug: "Aug",
      monthShortSep: "Sep",
      monthShortOct: "Oct",
      monthShortNov: "Nov",
      monthShortDec: "Dec",
      weekdaySun: "Sun",
      weekdayMon: "Mon",
      weekdayTue: "Tue",
      weekdayWed: "Wed",
      weekdayThu: "Thu",
      weekdayFri: "Fri",
      weekdaySat: "Sat",
      // Web - Daily Log & Trackers
      dailyLog: "Daily Log",
      addHabitPlaceholder: "Add a habit...",
      memorableMomentHeader: "Memorable Moment",
      oneLineMemorableMoment: "One-line memorable moment...",
      sleepTracker: "Sleep Tracker",
      moodTracker: "Mood Tracker",
      weightTracker: "Weight Tracker",
      sleepForMonth: "Sleep for {{month}} {{year}}",
      moodForMonth: "Mood for {{month}} {{year}}",
      weightForMonth: "Weight for {{month}} {{year}}",
      hrsAvg: "hrs avg",
      overall: "overall",
      last: "last",
      sleepHrsHeader: "Sleep (hrs)",
      weightHeaderUnit: "Weight ({{unit}})",
      enterSleepHours: "Enter sleep hours in the table below",
      habitTracker: "Habit Tracker",
      trackersHome: "Trackers",
      habit: "Habit",
      clickDayCycleMood: "Click a day to cycle mood",
      timeline: "Timeline",
      // Insights
      insightsSubtitle: "Track your consistency and see your long-term progress.",
      previousMonth: "Previous month",
      nextMonth: "Next month",
      consistencyScore: "Consistency Score",
      up: "UP",
      sleep: "Sleep",
      sleepThisMonth: "This month (hours per night)",
      weightThisMonth: "This month ({{unit}})",
      average: "Average",
      min: "Min",
      max: "Max",
      emptySleep: "No sleep data this month yet",
      emptyWeight: "No weight data this month yet",
      changeThisMonth: "Change this month",
      moodCorrelation: "Journal Mood Correlation",
      moodCorrelationSub: "Impact of your daily habits on your emotional wellbeing",
      daySingle: "Day",
      daysPlural: "Days",
      emptyMoodInsights: "No mood data this month",
      insightEngine: "Insight Engine",
      insightText: "Your most frequent mood this month is \"{{mood}}\". Keep tracking to uncover patterns between your sleep, habits, and how you feel.",
      streaks: "Streaks",
      daysJournaled: "Days journaled",
      current: "Current",
      longest: "Longest",
      emptyStreaks: "Add habits to track more streaks",
      monthLogs: "Month Logs",
      page: "Page",
      pageOf: "Page {{current}} of {{total}}",
      date: "Date",
      habitsDone: "Habits Done",
      previousPage: "Previous page",
      nextPage: "Next page",
      days: "days",
      // Tooltips & aria-labels
      voiceInputStart: "Start voice input",
      voiceInputStop: "Stop voice input",
      voiceInputUnsupported: "Voice input not supported",
      voiceInputListening: "Listening...",
      tooltipClickDayWrite: "👆 Click on any day to write a full journal entry!",
      tooltipClose: "Close tooltip",
      sleepChartHint: "Tip: Click to add points, click on existing points to adjust them",
      daysLoggedLabel: "Days Logged",
      change: "Change",
      entries: "Entries",
      setWeightGoal: "Set Weight Goal ({{unit}})",
      enterGoalKg: "Enter goal (kg)",
      enterGoalLbs: "Enter goal (lbs)",
      clearGoal: "Clear Goal",
      addYearTitle: "Add Year",
      enterYearPlaceholder: "Enter year (e.g., 2026)",
      pleaseEnterYear: "Please enter a year",
      pleaseEnterValidYear: "Please enter a valid year (1900-2100)",
      yearAlreadyExists: "Year already exists",
      monthLabel: "Month:",
      deleteYearsTitle: "Delete Years",
      deleteYearsWarning: "You are about to delete {{count}} year(s): {{years}}",
      deleteYearsCannotUndo: "This action cannot be undone. All journal entries, habits, and data for these years will be permanently lost.",
      typeToConfirm: "Type \"i understand\" to confirm:",
      deleteYearsPlaceholder: "i understand",
      deleting: "Deleting…",
      deleteYearsBtn: "Delete Years",
      removeHabitTitle: "Remove Habit?",
      removeHabitConfirmMobile: "Are you sure you want to remove {{habit}}? All completion history for this habit will be lost.",
      removeHabitConfirmWeb: "Are you sure you want to remove {{habit}}? This will delete the habit and its history from all days.",
      setReminder: "Set reminder",
      changeReminder: "Change reminder",
      removeReminder: "Remove reminder",
      hideFromFriends: "Hide from friends",
      hideFromFriendsSubtitle: "Only you can see this",
      habitCardColor: "Card color",
      habitCardColorDefault: "Default",
      habitCardColorCustom: "Custom",
      setReminderTitle: "Daily Reminder",
      setReminderDesc: "Get a notification for {{habit}} at:",
      deleteHabitTitle: "Delete habit?",
      dontWarnAgain: "Don't warn me again",
      deselectForDeletion: "Deselect for deletion",
      selectForDeletion: "Select for deletion",
      selectYear: "Select {{y}}",
      addYear: "Add year",
      removeYears: "Remove years",
      confirmDeleteYears: "Confirm delete selected years",
      deleteWithCount: "Delete ({{count}})",
      friends: "Friends",
      menu: "Menu",
      openMenu: "Open menu",
      add: "Add",
      previousDay: "Previous day",
      nextDay: "Next day",
      clear: "Clear",
      unfavourite: "Unfavourite",
      favourite: "Favourite",
      unfavouriteThisDay: "Unfavourite this day",
      favouriteThisDay: "Favourite this day",
      viewFullSizePhoto: "View full-size photo",
      uploadPhoto: "Upload photo",
      closeFullscreenImage: "Close fullscreen image",
      close: "Close",
      selectColor: "Select color {{color}}",
      customColor: "Custom color",
      // Location tags & Journal Map
      location: "Location",
      addLocation: "Add location",
      changeLocation: "Change location",
      removeLocation: "Remove location",
      searchLocation: "Search for a city or place…",
      useMyLocation: "Use my current location",
      locating: "Locating…",
      locationOffline: "Location search needs an internet connection.",
      locationSearchFailed: "Couldn't find that place. Try again.",
      locationPermissionDenied: "Location access was denied. You can still search for a place.",
      noLocationResults: "No places found.",
      journalMap: "Journal Map",
      journalMapDesc: "See every country you've journaled in.",
      journalMapSummary: "You've journaled in {{countries}} countries · {{entries}} tagged entries",
      showWholeWorld: "Whole world",
      entryCount: "{{count}} entries",
      entryCount_one: "{{count}} entry",
      countriesVisited: "{{count}} countries",
      countriesVisited_one: "{{count}} country",
      selectCountryHint: "Tap a highlighted country or a pin to see what you wrote there.",
      noLocationsYet: "No places yet",
      noLocationsYetDesc: "Add a location to a journal entry and it will show up on this map.",
      noTextForDay: "No text for this day",
      eraser: "Eraser",
      undo: "Undo",
      switchToWhiteBg: "Switch to white background",
      switchToDarkBg: "Switch to dark background",
      levelAndXP: "Level & XP",
      moreOptions: "More options",
      notCompletedYet: "Not completed yet",
      searchFavouriteEntries: "Search favourite entries",
      clearSearch: "Clear search",
      favouriteThisMoment: "Favourite this moment",
      markHabitIncomplete: "Mark {{habit}} incomplete",
      markHabitComplete: "Mark {{habit}} complete",
      levelTooltip: "Level {{level}}",
      toggleTheme: "Toggle theme",
      journalStreakTooltip: "Journaling streak – how many consecutive days you've written",
      weightUnitLabel: "Weight unit",
      today: "Today",
      done: "Done",
      // Friends
      backToJournal: "Back to Journal",
      friendsSetupRequired: "Database Setup Required",
      friendsSetupRun: "Run FRIENDS_SETUP.sql in your Supabase SQL Editor to enable friends.",
      displayName: "Display Name",
      editProfile: "Edit Profile",
      experience: "Experience",
      requests: "Requests",
      findFriends: "Find Friends",
      loadingFriends: "Loading friends...",
      noFriendsYet: "No friends yet",
      friendsSearchHint: "Search for friends by username or email to connect and see their habits!",
      removeFriend: "Remove friend",
      completedAt: "Completed at {{time}}",
      todaysProgress: "Today's progress:",
      noHabitsTracked: "No habits tracked yet",
      receivedRequests: "Received Requests",
      sentRequests: "Sent Requests",
      noPendingRequests: "No pending requests",
      accept: "Accept",
      decline: "Decline",
      searchFriendsPlaceholder: "Search by username or email...",
      searching: "Searching...",
      searchMinChars: "Type at least 2 characters to search",
      noUsersFound: "No users found matching \"{{query}}\"",
      friendsAlready: "Friends ✓",
      pending: "Pending",
      addFriend: "Add Friend",
      friendsInviteFooter: "Invite friends to earn bonus XP and stay motivated!",
      removeFriendTitle: "Remove friend?",
      removeFriendConfirm: "Remove {{name}} from your friends list? They will need to send a new request to connect again.",
      remove: "Remove",
      setYourName: "Set your name",
      usernameLabel: "Username",
      usernamePlaceholder: "username",
      usernameEmpty: "Username cannot be empty",
      usernameTaken: "Username already taken",
      profileUpdateFailed: "Failed to update profile. Please try again.",
      friendRequestFailed: "Failed to send friend request. Please try again.",
      syncing: "Syncing...",
      offline: "Offline",
      // Weekly leaderboard
      leaderboard: "Leaderboard",
      weeklyLeaderboard: "Weekly Competition",
      leaderboardWinnerReward: "🏆 Winner gets {{xp}} XP!",
      leaderboardResetsIn: "Resets in {{days}}d {{hours}}h {{mins}}m",
      leaderboardNoData: "Complete habits to earn XP and climb the ranks!",
      leaderboardNeedsFriends: "Add a friend to compete!",
      leaderboardInviteFriend: "Invite a friend to join the weekly competition. Earn the most XP each week to win 400 XP!",
      you: "you",
      xpShort: "XP",
      // Mood Year Matrix
      moodMatrixTitle: "Year in Moods",
      moodMatrixDesc: "See your mood across the entire year",
      moodMatrixNoEntry: "No journal entry for this day",
      moodMatrixOpenEntry: "Open full entry",
      moodMatrixTracked: "{{count}} moods tracked in {{year}}",
      previousYear: "Previous year",
      nextYear: "Next year",
      tutorialAppTutorial: "App Tutorial",
      tutorialAppTutorialDesc: "Take a quick tour of the app",
      tutorialChooseLanguage: "Choose your language",
      tutorialChooseLanguageDesc: "You can change it any time in Settings.",
      tutorialContinue: "Continue",
      tutorialBack: "Back",
      tutorialNext: "Next",
      tutorialSkip: "Skip",
      tutorialGetStarted: "Get Started",
      tutorialClose: "Close tutorial",
      tutorialStep1Title: "Your Journal Home",
      tutorialStep1Desc: "This is your daily home screen. Capture a memorable moment for the day, then tap Write Journal to tell the full story. Use the bottom nav to move between Journal, Habits, Trackers, Friends, and Settings.",
      tutorialStep2Title: "Write a Journal Entry",
      tutorialStep2Desc: "Pour your thoughts into a full entry. Use the microphone for voice input, sketch your day with the brush, or attach a photo to capture the moment. Everything auto-saves as you type.",
      tutorialStep3Title: "Track Your Habits",
      tutorialStep3Desc: "Add the habits you want to build and check them off each day. Stay consistent to earn XP, level up, and watch your streaks grow over time.",
      tutorialStep4Title: "Daily Trackers",
      tutorialStep4Desc: "Log your sleep, mood, and weight each day. Your data builds beautiful trends and a Mood Calendar so you can see patterns across the month.",
      tutorialStep5Title: "Friends",
      tutorialStep5Desc: "Connect with friends by username or email to see each other's habit progress. Send and accept friend requests to stay motivated together.",
      tutorialStep6Title: "Weekly Tournament",
      tutorialStep6Desc: "Compete with your friends each week. Earn the most XP from journaling, habits, and trackers to win the tournament and bonus XP!",
      resetPassword: "Reset your password",
      resetPasswordSubtitle: "Choose a new password for your account.",
      newPassword: "New password",
      confirmNewPassword: "Confirm new password",
      updatePassword: "Update password",
      passwordUpdated: "Password updated. Redirecting…",
      resetLinkExpired: "This reset link is invalid or has expired.",
      requestNewResetLink: "Request a new reset link",
      passwordsDoNotMatch: "Passwords do not match",
      passwordMinLength: "Password must be at least 8 characters",
      passwordNeedsUppercase: "Password must contain at least one uppercase letter",
      passwordNeedsNumber: "Password must contain at least one number",
      passwordMustBeNew: "Please choose a password you haven't used before.",
      passwordUpdateFailed: "Could not update password. Try again.",
      searchPlaceholder: "Search moments, entries, and sketches",
      globalSearch: "Search journal",
      searchMoment: "Moment",
      searchEntry: "Entry",
      searchSketch: "Sketch",
      exportData: "Export",
      exportMarkdown: "Download Markdown",
      exportJson: "Download JSON",
      exportDesc: "Save a copy of your journal, habits, and trackers.",
      exporting: "Preparing download…",
      journalReminder: "Journal reminder",
      journalReminderDesc: "Get a nudge if you haven't written yet.",
      journalReminderTime: "Reminder time",
      enableReminder: "Enable reminder",
      notificationsBlocked: "Notifications are blocked in this browser.",
      writeToday: "Write today's entry",
    }
  },
  es: {
    translation: {
      settings: "Ajustes",
      appearance: "Apariencia",
      theme: "Tema",
      darkMode: "Modo oscuro",
      lightMode: "Modo claro",
      language: "Idioma",
      navigation: "Navegación",
      dashboard: "Panel",
      openDashboard: "Abrir panel",
      dashboardDesc: "Estadísticas y resumen del mes",
      insightsTrends: "Análisis y tendencias",
      insightsDesc: "Ver análisis y progreso",
      account: "Cuenta",
      signOut: "Cerrar sesión",
      installApp: "Descargar app",
      installAppDesc: "Instala Life Journal en tu dispositivo para acceso rápido",
      installAppIOS: "Toca el botón Compartir y luego \"Añadir a la pantalla de inicio\"",
      quickAccess: "Acceso rápido a tus estadísticas.",
      journal: "Diario",
      name: "Nombre",
      favouriteMoments: "Momentos favoritos",
      sketchGallery: "Galería de bocetos",
      sketchGallerySubtitle: "Todos tus bocetos en un solo lugar.",
      allJournalEntries: "Todas las entradas del diario",
      allJournalEntriesSubtitle: "Explora y busca todas tus entradas del diario.",
      allJournalEntriesCount: "{{shown}} de {{total}} entradas",
      noJournalEntriesYet: "Aún no hay entradas en el diario.",
      noJournalEntriesHint: "Escribe en tu diario para ver las entradas aquí.",
      search: "Buscar",
      searchByDate: "Buscar por fecha...",
      searchSketches: "Buscar bocetos",
      noSketchesYet: "Aún no hay bocetos.",
      noSketchesHint: "Dibuja tu día desde el diario para verlo aquí.",
      noMatchesFor: "Sin resultados para \"{{query}}\"",
      sketchFrom: "Boceto del {{date}}",
      viewEntry: "Ver entrada",
      sketchCount: "{{count}} de {{total}} boceto",
      units: "Unidades",
      weight: "Peso",
      totalXP: "XP total",
      level: "Nivel",
      levelUp: "Subir de nivel",
      editJournalSettings: "Editar configuración del diario",
      editJournalName: "Editar nombre del diario",
      renameJournal: "Renombrar diario",
      enterJournalName: "Ingresa el nombre del diario",
      journalNameCannotBeEmpty: "El nombre del diario no puede estar vacío",
      characters: "caracteres",
      cancel: "Cancelar",
      save: "Guardar",
      journalTab: "Diario",
      journalAndHabits: "Diario y hábitos",
      habitsTab: "Habitos",
      trackersTab: "Seguimiento",
      friendsTab: "Amigos",
      settingsTab: "Ajustes",
      back: "Volver",
      loading: "Cargando...",
      verifyEmail: "Verifica tu correo",
      verifySubtitle: "Enviamos un enlace de verificación a",
      verifyCheck: "Revisa tu bandeja de entrada y haz clic en el enlace.",
      verifiedBtn: "Ya verifiqué mi correo",
      signOutDifferent: "Cerrar sesión y usar otra cuenta",
      photo: "Foto",
      addPhoto: "Agregar foto",
      addPhotoTypes: "Agregar foto (JPEG, PNG o WebP)",
      removePhoto: "Quitar foto",
      sketchPad: "Bocetos",
      writeJournal: "Escribir diario",
      tellStory: "Cuenta tu historia",
      sketchDay: "Dibujar el día",
      doodleThoughts: "Garabatea tus pensamientos",
      captureMoment: "Captura el momento",
      memorableMoments: "Momentos memorables",
      whatMadeSpecial: "¿Qué hizo especial este día?",
      journalEntry: "Entrada del diario",
      yourThoughts: "Tus pensamientos...",
      pourThoughtsPlaceholder: "Vierte tus pensamientos aquí...",
      addNewHabitPlaceholder: "Añadir un nuevo hábito...",
      todaysProgressLabel: "Progreso de hoy",
      memorableMoment: "Momento memorable...",
      statsFor: "Estadísticas de {{month}} {{year}}",
      journalEntries: "Entradas del diario",
      ofDays: "de {{n}} días",
      memorableMomentsCount: "Momentos memorables",
      habitsCompleted: "Habitos completados",
      ofGoal: "{{pct}}% del objetivo",
      noHabitsSet: "Sin hábitos configurados",
      xpEarnedThisMonth: "XP ganadas este mes",
      consistency: "Consistencia",
      journalStreak: "Racha de diario",
      xpEarned: "XP ganadas",
      moodAvg: "Ánimo promedio",
      levelInLevel: "Niv.{{level}} · {{current}}/{{total}} en nivel",
      last7DaysTrend: "Tendencia últimos 7 días",
      levelTotal: "Nivel {{level}} total",
      avgSleep: "Prom. sueño",
      daysLogged: "{{n}} días registrados",
      noData: "Sin datos",
      logsCount: "{{n}} registros",
      moodDistribution: "Distribución del ánimo",
      noMoodData: "Sin datos de ánimo este mes",
      noMoodSet: "sin ánimo definido",
      sleepOverMonth: "Sueño del mes (horas)",
      eightHourGoal: "— Línea verde = meta 8h",
      noSleepData: "Sin datos de sueño este mes",
      weightTrend: "Tendencia de peso ({{unit}})",
      habitCompletions: "Habitos completados este mes",
      habitCompletionsDesc: "Veces completado cada hábito en {{month}} {{year}}",
      moodBreakdown: "Desglose del ánimo",
      dayLabel: "Día {{n}}",
      moodTooltip: "Ánimo",
      mood: "Ánimo",
      sleepTooltip: "Sueño",
      weightTooltip: "Peso",
      moodCalendar: "Calendario de ánimo",
      tapToSet: "Toca para establecer",
      tapDayCycleMood: "Toca un día para cambiar el ánimo",
      noTrackerData: "Aún no hay datos este mes. ¡Empieza a registrar arriba!",
      sleepHoursLabel: "Sueño (horas)",
      saved: "Guardado",
      uploading: "Subiendo...",
      hours: "horas",
      hrs: "h",
      moodEcstatic: "Extasiado",
      moodHappy: "Feliz",
      moodNeutral: "Neutral",
      moodSad: "Triste",
      moodAngry: "Enojado",
      monthJanuary: "Enero",
      monthFebruary: "Febrero",
      monthMarch: "Marzo",
      monthApril: "Abril",
      monthMay: "Mayo",
      monthJune: "Junio",
      monthJuly: "Julio",
      monthAugust: "Agosto",
      monthSeptember: "Septiembre",
      monthOctober: "Octubre",
      monthNovember: "Noviembre",
      monthDecember: "Diciembre",
      monthShortJan: "Ene",
      monthShortFeb: "Feb",
      monthShortMar: "Mar",
      monthShortApr: "Abr",
      monthShortMay: "May",
      monthShortJun: "Jun",
      monthShortJul: "Jul",
      monthShortAug: "Ago",
      monthShortSep: "Sep",
      monthShortOct: "Oct",
      monthShortNov: "Nov",
      monthShortDec: "Dic",
      weekdaySun: "Dom",
      weekdayMon: "Lun",
      weekdayTue: "Mar",
      weekdayWed: "Mié",
      weekdayThu: "Jue",
      weekdayFri: "Vie",
      weekdaySat: "Sáb",
      dailyLog: "Registro diario",
      addHabitPlaceholder: "Añadir un hábito...",
      memorableMomentHeader: "Momento memorable",
      oneLineMemorableMoment: "Momento memorable en una línea...",
      sleepTracker: "Seguimiento del sueño",
      moodTracker: "Seguimiento del ánimo",
      weightTracker: "Seguimiento del peso",
      sleepForMonth: "Sueño para {{month}} {{year}}",
      moodForMonth: "Ánimo para {{month}} {{year}}",
      weightForMonth: "Peso para {{month}} {{year}}",
      hrsAvg: "h promedio",
      overall: "general",
      last: "último",
      sleepHrsHeader: "Sueño (h)",
      weightHeaderUnit: "Peso ({{unit}})",
      enterSleepHours: "Introduce las horas de sueño en la tabla",
      habitTracker: "Seguimiento de hábitos",
      trackersHome: "Seguimientos",
      habit: "Hábito",
      clickDayCycleMood: "Haz clic en un día para cambiar el ánimo",
      timeline: "Cronología",
      insightsSubtitle: "Rastrea tu consistencia y ver tu progreso a largo plazo.",
      previousMonth: "Mes anterior",
      nextMonth: "Mes siguiente",
      consistencyScore: "Puntuación de consistencia",
      up: "ARRIBA",
      sleep: "Sueño",
      sleepThisMonth: "Este mes (horas por noche)",
      weightThisMonth: "Este mes ({{unit}})",
      average: "Promedio",
      min: "Mín",
      max: "Máx",
      emptySleep: "Sin datos de sueño este mes aún",
      emptyWeight: "Sin datos de peso este mes aún",
      changeThisMonth: "Cambio este mes",
      moodCorrelation: "Correlación de ánimo",
      moodCorrelationSub: "Impacto de tus hábitos diarios en tu bienestar emocional",
      daySingle: "Día",
      daysPlural: "Días",
      emptyMoodInsights: "Sin datos de ánimo este mes",
      insightEngine: "Motor de insights",
      insightText: "Tu ánimo más frecuente este mes es \"{{mood}}\". Sigue registrando para descubrir patrones.",
      streaks: "Rachas",
      daysJournaled: "Días con diario",
      current: "Actual",
      longest: "Más larga",
      emptyStreaks: "Añade hábitos para seguir más rachas",
      monthLogs: "Registros del mes",
      page: "Página",
      pageOf: "Página {{current}} de {{total}}",
      date: "Fecha",
      habitsDone: "Habitos hechos",
      previousPage: "Página anterior",
      nextPage: "Página siguiente",
      days: "días",
      voiceInputStart: "Iniciar entrada de voz",
      voiceInputStop: "Detener entrada de voz",
      voiceInputUnsupported: "Entrada de voz no compatible",
      voiceInputListening: "Escuchando...",
      tooltipClickDayWrite: "👆 ¡Haz clic en cualquier día para escribir una entrada completa!",
      tooltipClose: "Cerrar tooltip",
      sleepChartHint: "Consejo: Haz clic para añadir puntos, clic en puntos existentes para ajustarlos",
      daysLoggedLabel: "Días registrados",
      change: "Cambio",
      entries: "Entradas",
      setWeightGoal: "Objetivo de peso ({{unit}})",
      enterGoalKg: "Objetivo (kg)",
      enterGoalLbs: "Objetivo (lbs)",
      clearGoal: "Borrar objetivo",
      addYearTitle: "Añadir año",
      enterYearPlaceholder: "Año (ej., 2026)",
      pleaseEnterYear: "Por favor introduce un año",
      pleaseEnterValidYear: "Introduce un año válido (1900-2100)",
      yearAlreadyExists: "Ese año ya existe",
      monthLabel: "Mes:",
      deleteYearsTitle: "Eliminar años",
      deleteYearsWarning: "Vas a eliminar {{count}} año(s): {{years}}",
      deleteYearsCannotUndo: "Esta acción no se puede deshacer. Todas las entradas, hábitos y datos de estos años se perderán permanentemente.",
      typeToConfirm: "Escribe \"i understand\" para confirmar:",
      deleteYearsPlaceholder: "i understand",
      deleting: "Eliminando…",
      deleteYearsBtn: "Eliminar años",
      removeHabitTitle: "¿Eliminar hábito?",
      removeHabitConfirmMobile: "¿Seguro que quieres eliminar {{habit}}? Se perderá todo el historial de completado.",
      removeHabitConfirmWeb: "¿Seguro que quieres eliminar {{habit}}? Esto borrará el hábito y su historial de todos los días.",
      setReminder: "Establecer recordatorio",
      changeReminder: "Cambiar recordatorio",
      removeReminder: "Quitar recordatorio",
      hideFromFriends: "Ocultar a amigos",
      hideFromFriendsSubtitle: "Solo tú puedes ver esto",
      habitCardColor: "Color de tarjeta",
      habitCardColorDefault: "Predeterminado",
      habitCardColorCustom: "Personalizado",
      setReminderTitle: "Recordatorio diario",
      setReminderDesc: "Recibe una notificación para {{habit}} a las:",
      deleteHabitTitle: "¿Eliminar hábito?",
      dontWarnAgain: "No volver a advertir",
      deselectForDeletion: "Deseleccionar para eliminar",
      selectForDeletion: "Seleccionar para eliminar",
      selectYear: "Seleccionar {{y}}",
      addYear: "Añadir año",
      removeYears: "Eliminar años",
      confirmDeleteYears: "Confirmar eliminar años seleccionados",
      deleteWithCount: "Eliminar ({{count}})",
      friends: "Amigos",
      menu: "Menú",
      openMenu: "Abrir menú",
      add: "Añadir",
      previousDay: "Día anterior",
      nextDay: "Día siguiente",
      clear: "Limpiar",
      unfavourite: "Quitar de favoritos",
      favourite: "Favorito",
      unfavouriteThisDay: "Quitar este día de favoritos",
      favouriteThisDay: "Añadir este día a favoritos",
      viewFullSizePhoto: "Ver foto a tamaño completo",
      uploadPhoto: "Subir foto",
      closeFullscreenImage: "Cerrar imagen a pantalla completa",
      close: "Cerrar",
      selectColor: "Seleccionar color {{color}}",
      customColor: "Color personalizado",
      // Etiquetas de ubicación y mapa del diario
      location: "Ubicación",
      addLocation: "Añadir ubicación",
      changeLocation: "Cambiar ubicación",
      removeLocation: "Quitar ubicación",
      searchLocation: "Busca una ciudad o lugar…",
      useMyLocation: "Usar mi ubicación actual",
      locating: "Localizando…",
      locationOffline: "La búsqueda de lugares necesita conexión a internet.",
      locationSearchFailed: "No se encontró ese lugar. Inténtalo de nuevo.",
      locationPermissionDenied: "Se denegó el acceso a la ubicación. Aún puedes buscar un lugar.",
      noLocationResults: "No se encontraron lugares.",
      journalMap: "Mapa del diario",
      journalMapDesc: "Mira todos los países donde has escrito.",
      journalMapSummary: "Has escrito en {{countries}} países · {{entries}} entradas con ubicación",
      showWholeWorld: "Todo el mundo",
      entryCount: "{{count}} entradas",
      entryCount_one: "{{count}} entrada",
      countriesVisited: "{{count}} países",
      countriesVisited_one: "{{count}} país",
      selectCountryHint: "Toca un país resaltado o un marcador para ver lo que escribiste allí.",
      noLocationsYet: "Aún no hay lugares",
      noLocationsYetDesc: "Añade una ubicación a una entrada y aparecerá en este mapa.",
      noTextForDay: "Sin texto para este día",
      eraser: "Goma",
      undo: "Deshacer",
      switchToWhiteBg: "Cambiar a fondo blanco",
      switchToDarkBg: "Cambiar a fondo oscuro",
      levelAndXP: "Nivel y XP",
      moreOptions: "Más opciones",
      notCompletedYet: "Aún no completado",
      searchFavouriteEntries: "Buscar entradas favoritas",
      clearSearch: "Borrar búsqueda",
      favouriteThisMoment: "Añadir este momento a favoritos",
      markHabitIncomplete: "Marcar {{habit}} como incompleto",
      markHabitComplete: "Marcar {{habit}} como completo",
      levelTooltip: "Nivel {{level}}",
      toggleTheme: "Alternar tema",
      journalStreakTooltip: "Racha de diario – días consecutivos que has escrito",
      weightUnitLabel: "Unidad de peso",
      today: "Hoy",
      done: "Listo",
      backToJournal: "Volver al diario",
      friendsSetupRequired: "Configuración de base de datos requerida",
      friendsSetupRun: "Ejecuta FRIENDS_SETUP.sql en el editor SQL de Supabase para activar amigos.",
      displayName: "Nombre para mostrar",
      editProfile: "Editar perfil",
      experience: "Experiencia",
      requests: "Solicitudes",
      findFriends: "Buscar amigos",
      loadingFriends: "Cargando amigos...",
      noFriendsYet: "Aún no tienes amigos",
      friendsSearchHint: "¡Busca amigos por usuario o correo para conectar y ver sus hábitos!",
      removeFriend: "Eliminar amigo",
      completedAt: "Completado a las {{time}}",
      todaysProgress: "Progreso de hoy:",
      noHabitsTracked: "Sin hábitos registrados aún",
      receivedRequests: "Solicitudes recibidas",
      sentRequests: "Solicitudes enviadas",
      noPendingRequests: "Sin solicitudes pendientes",
      accept: "Aceptar",
      decline: "Rechazar",
      searchFriendsPlaceholder: "Buscar por usuario o correo...",
      searching: "Buscando...",
      searchMinChars: "Escribe al menos 2 caracteres para buscar",
      noUsersFound: "No se encontraron usuarios que coincidan con \"{{query}}\"",
      friendsAlready: "Amigos ✓",
      pending: "Pendiente",
      addFriend: "Añadir amigo",
      friendsInviteFooter: "¡Invita amigos para ganar XP extra y mantener la motivación!",
      removeFriendTitle: "¿Eliminar amigo?",
      removeFriendConfirm: "¿Eliminar a {{name}} de tu lista de amigos? Tendrá que enviar una nueva solicitud para conectar de nuevo.",
      remove: "Eliminar",
      setYourName: "Establece tu nombre",
      usernameLabel: "Usuario",
      usernamePlaceholder: "usuario",
      usernameEmpty: "El usuario no puede estar vacío",
      usernameTaken: "El usuario ya está en uso",
      profileUpdateFailed: "Error al actualizar el perfil. Inténtalo de nuevo.",
      friendRequestFailed: "Error al enviar la solicitud. Inténtalo de nuevo.",
      syncing: "Sincronizando...",
      offline: "Sin conexión",
      leaderboard: "Clasificación",
      weeklyLeaderboard: "Competencia Semanal",
      leaderboardWinnerReward: "🏆 ¡El ganador obtiene {{xp}} XP!",
      leaderboardResetsIn: "Reinicia en {{days}}d {{hours}}h {{mins}}m",
      leaderboardNoData: "¡Completa hábitos para ganar XP y subir en el ranking!",
      leaderboardNeedsFriends: "¡Añade un amigo para competir!",
      leaderboardInviteFriend: "Invita a un amigo a la competencia semanal. ¡Gana más XP cada semana para ganar 400 XP!",
      you: "tú",
      xpShort: "XP",
      moodMatrixTitle: "Año en estados de ánimo",
      moodMatrixDesc: "Ve tu ánimo a lo largo del año",
      moodMatrixNoEntry: "Sin entrada de diario para este día",
      moodMatrixOpenEntry: "Abrir entrada completa",
      moodMatrixTracked: "{{count}} ánimos registrados en {{year}}",
      previousYear: "Año anterior",
      nextYear: "Año siguiente",
      tutorialAppTutorial: "Tutorial de la app",
      tutorialAppTutorialDesc: "Haz un recorrido rápido por la app",
      tutorialChooseLanguage: "Elige tu idioma",
      tutorialChooseLanguageDesc: "Puedes cambiarlo en cualquier momento en Ajustes.",
      tutorialContinue: "Continuar",
      tutorialBack: "Atrás",
      tutorialNext: "Siguiente",
      tutorialSkip: "Omitir",
      tutorialGetStarted: "Comenzar",
      tutorialClose: "Cerrar tutorial",
      tutorialStep1Title: "Tu inicio del diario",
      tutorialStep1Desc: "Esta es tu pantalla de inicio diaria. Captura un momento memorable del día y luego toca Escribir diario para contar toda la historia. Usa la barra inferior para moverte entre Diario, Hábitos, Seguidores, Amigos y Ajustes.",
      tutorialStep2Title: "Escribe una entrada",
      tutorialStep2Desc: "Vuelca tus pensamientos en una entrada completa. Usa el micrófono para entrada de voz, dibuja tu día con el pincel o adjunta una foto para capturar el momento. Todo se guarda automáticamente mientras escribes.",
      tutorialStep3Title: "Sigue tus hábitos",
      tutorialStep3Desc: "Añade los hábitos que quieras construir y márcalos cada día. Mantén la constancia para ganar XP, subir de nivel y ver crecer tus rachas con el tiempo.",
      tutorialStep4Title: "Seguidores diarios",
      tutorialStep4Desc: "Registra tu sueño, ánimo y peso cada día. Tus datos crean tendencias preciosas y un calendario de ánimo para que veas los patrones del mes.",
      tutorialStep5Title: "Amigos",
      tutorialStep5Desc: "Conecta con amigos por nombre de usuario o correo para ver el progreso de hábitos del otro. Envía y acepta solicitudes para manteneros motivados juntos.",
      tutorialStep6Title: "Torneo semanal",
      tutorialStep6Desc: "Compite con tus amigos cada semana. Gana más XP del diario, los hábitos y los seguidores para ganar el torneo y XP extra.",
      resetPassword: "Restablecer tu contraseña",
      resetPasswordSubtitle: "Elige una nueva contraseña para tu cuenta.",
      newPassword: "Nueva contraseña",
      confirmNewPassword: "Confirmar nueva contraseña",
      updatePassword: "Actualizar contraseña",
      passwordUpdated: "Contraseña actualizada. Redirigiendo…",
      resetLinkExpired: "Este enlace no es válido o ha caducado.",
      requestNewResetLink: "Pedir un nuevo enlace",
      passwordsDoNotMatch: "Las contraseñas no coinciden",
      passwordMinLength: "La contraseña debe tener al menos 8 caracteres",
      passwordNeedsUppercase: "La contraseña debe incluir al menos una mayúscula",
      passwordNeedsNumber: "La contraseña debe incluir al menos un número",
      passwordMustBeNew: "Elige una contraseña que no hayas usado antes.",
      passwordUpdateFailed: "No se pudo actualizar la contraseña. Inténtalo de nuevo.",
      searchPlaceholder: "Buscar momentos, entradas y bocetos",
      globalSearch: "Buscar en el diario",
      searchMoment: "Momento",
      searchEntry: "Entrada",
      searchSketch: "Boceto",
      exportData: "Exportar",
      exportMarkdown: "Descargar Markdown",
      exportJson: "Descargar JSON",
      exportDesc: "Guarda una copia de tu diario, hábitos y seguimientos.",
      exporting: "Preparando descarga…",
      journalReminder: "Recordatorio del diario",
      journalReminderDesc: "Un aviso si aún no has escrito hoy.",
      journalReminderTime: "Hora del recordatorio",
      enableReminder: "Activar recordatorio",
      notificationsBlocked: "Las notificaciones están bloqueadas en este navegador.",
      writeToday: "Escribir la entrada de hoy",
    }
  },
  fr: {
    translation: {
      settings: "Paramètres",
      appearance: "Apparence",
      theme: "Thème",
      darkMode: "Mode sombre",
      lightMode: "Mode clair",
      language: "Langue",
      navigation: "Navigation",
      dashboard: "Tableau de bord",
      openDashboard: "Ouvrir le tableau de bord",
      dashboardDesc: "Statistiques et vue d'ensemble du mois",
      insightsTrends: "Analyses et tendances",
      insightsDesc: "Voir les analyses et la progression",
      account: "Compte",
      signOut: "Déconnexion",
      installApp: "Télécharger l'app",
      installAppDesc: "Installez Life Journal sur votre appareil pour un accès rapide",
      installAppIOS: "Appuyez sur Partager, puis \"Sur l'écran d'accueil\"",
      quickAccess: "Accès rapide à vos statistiques.",
      journal: "Journal",
      name: "Nom",
      favouriteMoments: "Moments favoris",
      sketchGallery: "Galerie de croquis",
      sketchGallerySubtitle: "Tous vos croquis en un seul endroit.",
      allJournalEntries: "Toutes les entrées du journal",
      allJournalEntriesSubtitle: "Parcourez et recherchez toutes vos entrées de journal.",
      allJournalEntriesCount: "{{shown}} sur {{total}} entrées",
      noJournalEntriesYet: "Aucune entrée de journal pour le moment.",
      noJournalEntriesHint: "Écrivez dans votre journal pour voir les entrées ici.",
      search: "Rechercher",
      searchByDate: "Rechercher par date...",
      searchSketches: "Rechercher des croquis",
      noSketchesYet: "Pas encore de croquis.",
      noSketchesHint: "Dessinez votre journée depuis le journal pour la voir ici.",
      noMatchesFor: "Aucun résultat pour \"{{query}}\"",
      sketchFrom: "Croquis du {{date}}",
      viewEntry: "Voir l'entrée",
      sketchCount: "{{count}} sur {{total}} croquis",
      units: "Unités",
      weight: "Poids",
      totalXP: "XP total",
      level: "Niveau",
      levelUp: "Niveau supérieur",
      editJournalSettings: "Modifier les paramètres du journal",
      editJournalName: "Modifier le nom du journal",
      renameJournal: "Renommer le journal",
      enterJournalName: "Entrez le nom du journal",
      journalNameCannotBeEmpty: "Le nom du journal ne peut pas être vide",
      characters: "caractères",
      cancel: "Annuler",
      save: "Enregistrer",
      journalTab: "Journal",
      journalAndHabits: "Journal et habitudes",
      habitsTab: "Habitudes",
      trackersTab: "Suivi",
      friendsTab: "Amis",
      settingsTab: "Paramètres",
      back: "Retour",
      loading: "Chargement...",
      verifyEmail: "Vérifiez votre e-mail",
      verifySubtitle: "Nous avons envoyé un lien de vérification à",
      verifyCheck: "Vérifiez votre boîte de réception et cliquez sur le lien.",
      verifiedBtn: "J'ai vérifié mon e-mail",
      signOutDifferent: "Se déconnecter et utiliser un autre compte",
      photo: "Photo",
      addPhoto: "Ajouter une photo",
      addPhotoTypes: "Ajouter une photo (JPEG, PNG ou WebP)",
      removePhoto: "Supprimer la photo",
      sketchPad: "Croquis",
      writeJournal: "Écrire dans le journal",
      tellStory: "Racontez votre histoire",
      sketchDay: "Dessiner le jour",
      doodleThoughts: "Griffonnez vos pensées",
      captureMoment: "Capturez le moment",
      memorableMoments: "Moments mémorables",
      whatMadeSpecial: "Qu'est-ce qui a rendu ce jour spécial ?",
      journalEntry: "Entrée du journal",
      yourThoughts: "Vos pensées...",
      pourThoughtsPlaceholder: "Exprimez vos pensées ici...",
      addNewHabitPlaceholder: "Ajouter une nouvelle habitude...",
      todaysProgressLabel: "Progression du jour",
      memorableMoment: "Moment mémorable...",
      statsFor: "Statistiques pour {{month}} {{year}}",
      journalEntries: "Entrées du journal",
      ofDays: "sur {{n}} jours",
      memorableMomentsCount: "Moments mémorables",
      habitsCompleted: "Habitudes complétées",
      ofGoal: "{{pct}}% de l'objectif",
      noHabitsSet: "Aucune habitude définie",
      xpEarnedThisMonth: "XP gagnés ce mois",
      consistency: "Régularité",
      journalStreak: "Série de journal",
      xpEarned: "XP gagnés",
      moodAvg: "Humeur moy.",
      levelInLevel: "Niv.{{level}} · {{current}}/{{total}} dans le niveau",
      last7DaysTrend: "Tendance 7 derniers jours",
      levelTotal: "Niveau {{level}} total",
      avgSleep: "Moy. sommeil",
      daysLogged: "{{n}} jours enregistrés",
      noData: "Aucune donnée",
      logsCount: "{{n}} enregistrements",
      moodDistribution: "Répartition de l'humeur",
      noMoodData: "Aucune donnée d'humeur ce mois",
      noMoodSet: "pas d'humeur définie",
      sleepOverMonth: "Sommeil du mois (heures)",
      eightHourGoal: "— Ligne verte = objectif 8h",
      noSleepData: "Aucune donnée de sommeil ce mois",
      weightTrend: "Tendance du poids ({{unit}})",
      habitCompletions: "Habitudes complétées ce mois",
      habitCompletionsDesc: "Fois que chaque habitude a été complétée en {{month}} {{year}}",
      moodBreakdown: "Répartition de l'humeur",
      dayLabel: "Jour {{n}}",
      moodTooltip: "Humeur",
      mood: "Humeur",
      sleepTooltip: "Sommeil",
      weightTooltip: "Poids",
      moodCalendar: "Calendrier d'humeur",
      tapToSet: "Appuyez pour définir",
      tapDayCycleMood: "Appuyez sur un jour pour changer l'humeur",
      noTrackerData: "Pas encore de données ce mois. Commencez à enregistrer ci-dessus !",
      sleepHoursLabel: "Sommeil (heures)",
      saved: "Enregistré",
      uploading: "Téléversement...",
      hours: "heures",
      hrs: "h",
      moodEcstatic: "Extatique",
      moodHappy: "Heureux",
      moodNeutral: "Neutre",
      moodSad: "Triste",
      moodAngry: "En colère",
      monthJanuary: "Janvier",
      monthFebruary: "Février",
      monthMarch: "Mars",
      monthApril: "Avril",
      monthMay: "Mai",
      monthJune: "Juin",
      monthJuly: "Juillet",
      monthAugust: "Août",
      monthSeptember: "Septembre",
      monthOctober: "Octobre",
      monthNovember: "Novembre",
      monthDecember: "Décembre",
      monthShortJan: "Janv",
      monthShortFeb: "Févr",
      monthShortMar: "Mars",
      monthShortApr: "Avr",
      monthShortMay: "Mai",
      monthShortJun: "Juin",
      monthShortJul: "Juil",
      monthShortAug: "Août",
      monthShortSep: "Sept",
      monthShortOct: "Oct",
      monthShortNov: "Nov",
      monthShortDec: "Déc",
      weekdaySun: "Dim",
      weekdayMon: "Lun",
      weekdayTue: "Mar",
      weekdayWed: "Mer",
      weekdayThu: "Jeu",
      weekdayFri: "Ven",
      weekdaySat: "Sam",
      dailyLog: "Journal quotidien",
      addHabitPlaceholder: "Ajouter une habitude...",
      memorableMomentHeader: "Moment mémorable",
      oneLineMemorableMoment: "Moment mémorable en une ligne...",
      sleepTracker: "Suivi du sommeil",
      moodTracker: "Suivi de l'humeur",
      weightTracker: "Suivi du poids",
      sleepForMonth: "Sommeil pour {{month}} {{year}}",
      moodForMonth: "Humeur pour {{month}} {{year}}",
      weightForMonth: "Poids pour {{month}} {{year}}",
      hrsAvg: "h en moy.",
      overall: "global",
      last: "dernier",
      sleepHrsHeader: "Sommeil (h)",
      weightHeaderUnit: "Poids ({{unit}})",
      enterSleepHours: "Entrez les heures de sommeil dans le tableau ci-dessous",
      habitTracker: "Suivi des habitudes",
      trackersHome: "Suivis",
      habit: "Habitude",
      clickDayCycleMood: "Cliquez sur un jour pour changer l'humeur",
      timeline: "Chronologie",
      insightsSubtitle: "Suivez votre régularité et voyez vos progrès à long terme.",
      previousMonth: "Mois précédent",
      nextMonth: "Mois suivant",
      consistencyScore: "Score de régularité",
      up: "HAUT",
      sleep: "Sommeil",
      sleepThisMonth: "Ce mois (heures par nuit)",
      weightThisMonth: "Ce mois ({{unit}})",
      average: "Moyenne",
      min: "Min",
      max: "Max",
      emptySleep: "Aucune donnée de sommeil ce mois",
      emptyWeight: "Aucune donnée de poids ce mois",
      changeThisMonth: "Changement ce mois",
      moodCorrelation: "Corrélation d'humeur",
      moodCorrelationSub: "Impact de vos habitudes quotidiennes sur votre bien-être émotionnel",
      daySingle: "Jour",
      daysPlural: "Jours",
      emptyMoodInsights: "Aucune donnée d'humeur ce mois",
      insightEngine: "Moteur d'insights",
      insightText: "Votre humeur la plus fréquente ce mois est \"{{mood}}\". Continuez à suivre pour découvrir des modèles.",
      streaks: "Séries",
      daysJournaled: "Jours journalisés",
      current: "Actuel",
      longest: "Plus longue",
      emptyStreaks: "Ajoutez des habitudes pour suivre plus de séries",
      monthLogs: "Journaux du mois",
      page: "Page",
      pageOf: "Page {{current}} sur {{total}}",
      date: "Date",
      habitsDone: "Habitudes faites",
      previousPage: "Page précédente",
      nextPage: "Page suivante",
      days: "jours",
      voiceInputStart: "Démarrer la saisie vocale",
      voiceInputStop: "Arrêter la saisie vocale",
      voiceInputUnsupported: "Saisie vocale non prise en charge",
      voiceInputListening: "Écoute...",
      tooltipClickDayWrite: "👆 Cliquez sur n'importe quel jour pour écrire une entrée complète !",
      tooltipClose: "Fermer l'info-bulle",
      sleepChartHint: "Astuce : Cliquez pour ajouter des points, cliquez sur les points existants pour les ajuster",
      daysLoggedLabel: "Jours enregistrés",
      change: "Changement",
      entries: "Entrées",
      setWeightGoal: "Objectif de poids ({{unit}})",
      enterGoalKg: "Objectif (kg)",
      enterGoalLbs: "Objectif (lbs)",
      clearGoal: "Effacer l'objectif",
      addYearTitle: "Ajouter une année",
      enterYearPlaceholder: "Année (ex. 2026)",
      pleaseEnterYear: "Veuillez entrer une année",
      pleaseEnterValidYear: "Entrez une année valide (1900-2100)",
      yearAlreadyExists: "Cette année existe déjà",
      monthLabel: "Mois :",
      deleteYearsTitle: "Supprimer les années",
      deleteYearsWarning: "Vous êtes sur le point de supprimer {{count}} année(s) : {{years}}",
      deleteYearsCannotUndo: "Cette action est irréversible. Toutes les entrées, habitudes et données de ces années seront définitivement perdues.",
      typeToConfirm: "Tapez \"i understand\" pour confirmer :",
      deleteYearsPlaceholder: "i understand",
      deleting: "Suppression…",
      deleteYearsBtn: "Supprimer les années",
      removeHabitTitle: "Retirer l'habitude ?",
      removeHabitConfirmMobile: "Êtes-vous sûr de vouloir retirer {{habit}} ? Tout l'historique sera perdu.",
      removeHabitConfirmWeb: "Êtes-vous sûr de vouloir retirer {{habit}} ? Cela supprimera l'habitude et son historique de tous les jours.",
      setReminder: "Définir un rappel",
      changeReminder: "Modifier le rappel",
      removeReminder: "Supprimer le rappel",
      hideFromFriends: "Masquer aux amis",
      hideFromFriendsSubtitle: "Visible uniquement par vous",
      habitCardColor: "Couleur de la carte",
      habitCardColorDefault: "Par défaut",
      habitCardColorCustom: "Personnalisé",
      setReminderTitle: "Rappel quotidien",
      setReminderDesc: "Recevez une notification pour {{habit}} à :",
      deleteHabitTitle: "Supprimer l'habitude ?",
      dontWarnAgain: "Ne plus avertir",
      deselectForDeletion: "Désélectionner pour supprimer",
      selectForDeletion: "Sélectionner pour supprimer",
      selectYear: "Sélectionner {{y}}",
      addYear: "Ajouter une année",
      removeYears: "Supprimer des années",
      confirmDeleteYears: "Confirmer la suppression des années sélectionnées",
      deleteWithCount: "Supprimer ({{count}})",
      friends: "Amis",
      menu: "Menu",
      openMenu: "Ouvrir le menu",
      add: "Ajouter",
      previousDay: "Jour précédent",
      nextDay: "Jour suivant",
      clear: "Effacer",
      unfavourite: "Retirer des favoris",
      favourite: "Favori",
      unfavouriteThisDay: "Retirer ce jour des favoris",
      favouriteThisDay: "Ajouter ce jour aux favoris",
      viewFullSizePhoto: "Voir la photo en grand",
      uploadPhoto: "Téléverser une photo",
      closeFullscreenImage: "Fermer l'image plein écran",
      close: "Fermer",
      selectColor: "Sélectionner la couleur {{color}}",
      customColor: "Couleur personnalisée",
      // Lieux et carte du journal
      location: "Lieu",
      addLocation: "Ajouter un lieu",
      changeLocation: "Changer de lieu",
      removeLocation: "Supprimer le lieu",
      searchLocation: "Rechercher une ville ou un lieu…",
      useMyLocation: "Utiliser ma position actuelle",
      locating: "Localisation…",
      locationOffline: "La recherche de lieux nécessite une connexion internet.",
      locationSearchFailed: "Lieu introuvable. Réessayez.",
      locationPermissionDenied: "L'accès à la position a été refusé. Vous pouvez toujours rechercher un lieu.",
      noLocationResults: "Aucun lieu trouvé.",
      journalMap: "Carte du journal",
      journalMapDesc: "Découvrez tous les pays où vous avez écrit.",
      journalMapSummary: "Vous avez écrit dans {{countries}} pays · {{entries}} entrées localisées",
      showWholeWorld: "Monde entier",
      entryCount: "{{count}} entrées",
      entryCount_one: "{{count}} entrée",
      countriesVisited: "{{count}} pays",
      countriesVisited_one: "{{count}} pays",
      selectCountryHint: "Touchez un pays surligné ou un repère pour voir ce que vous y avez écrit.",
      noLocationsYet: "Aucun lieu pour l'instant",
      noLocationsYetDesc: "Ajoutez un lieu à une entrée et il apparaîtra sur cette carte.",
      noTextForDay: "Pas de texte pour ce jour",
      eraser: "Gomme",
      undo: "Annuler",
      switchToWhiteBg: "Passer au fond blanc",
      switchToDarkBg: "Passer au fond sombre",
      levelAndXP: "Niveau et XP",
      moreOptions: "Plus d'options",
      notCompletedYet: "Pas encore complété",
      searchFavouriteEntries: "Rechercher les entrées favorites",
      clearSearch: "Effacer la recherche",
      favouriteThisMoment: "Ajouter ce moment aux favoris",
      markHabitIncomplete: "Marquer {{habit}} comme incomplet",
      markHabitComplete: "Marquer {{habit}} comme complet",
      levelTooltip: "Niveau {{level}}",
      toggleTheme: "Changer le thème",
      journalStreakTooltip: "Série de journal – jours consécutifs écrits",
      weightUnitLabel: "Unité de poids",
      today: "Aujourd'hui",
      done: "Terminé",
      backToJournal: "Retour au journal",
      friendsSetupRequired: "Configuration de la base de données requise",
      friendsSetupRun: "Exécutez FRIENDS_SETUP.sql dans l'éditeur SQL Supabase pour activer les amis.",
      displayName: "Nom d'affichage",
      editProfile: "Modifier le profil",
      experience: "Expérience",
      requests: "Demandes",
      findFriends: "Trouver des amis",
      loadingFriends: "Chargement des amis...",
      noFriendsYet: "Pas encore d'amis",
      friendsSearchHint: "Recherchez des amis par nom d'utilisateur ou e-mail pour vous connecter et voir leurs habitudes !",
      removeFriend: "Retirer l'ami",
      completedAt: "Terminé à {{time}}",
      todaysProgress: "Progression du jour :",
      noHabitsTracked: "Aucune habitude suivie pour l'instant",
      receivedRequests: "Demandes reçues",
      sentRequests: "Demandes envoyées",
      noPendingRequests: "Aucune demande en attente",
      accept: "Accepter",
      decline: "Refuser",
      searchFriendsPlaceholder: "Rechercher par nom d'utilisateur ou e-mail...",
      searching: "Recherche...",
      searchMinChars: "Tapez au moins 2 caractères pour rechercher",
      noUsersFound: "Aucun utilisateur trouvé pour \"{{query}}\"",
      friendsAlready: "Amis ✓",
      pending: "En attente",
      addFriend: "Ajouter un ami",
      friendsInviteFooter: "Invitez des amis pour gagner des XP bonus et rester motivé !",
      removeFriendTitle: "Retirer cet ami ?",
      removeFriendConfirm: "Retirer {{name}} de votre liste d'amis ? Ils devront envoyer une nouvelle demande pour se reconnecter.",
      remove: "Retirer",
      setYourName: "Définissez votre nom",
      usernameLabel: "Nom d'utilisateur",
      usernamePlaceholder: "nom d'utilisateur",
      usernameEmpty: "Le nom d'utilisateur ne peut pas être vide",
      usernameTaken: "Ce nom d'utilisateur est déjà pris",
      profileUpdateFailed: "Échec de la mise à jour du profil. Veuillez réessayer.",
      friendRequestFailed: "Échec de l'envoi de la demande. Veuillez réessayer.",
      syncing: "Synchronisation...",
      offline: "Hors ligne",
      leaderboard: "Classement",
      weeklyLeaderboard: "Compétition Hebdomadaire",
      leaderboardWinnerReward: "🏆 Le gagnant obtient {{xp}} XP !",
      leaderboardResetsIn: "Réinitialisation dans {{days}}j {{hours}}h {{mins}}m",
      leaderboardNoData: "Complétez des habitudes pour gagner de l'XP et grimper au classement !",
      leaderboardNeedsFriends: "Ajoutez un ami pour participer !",
      leaderboardInviteFriend: "Invitez un ami à rejoindre la compétition hebdomadaire. Gagnez le plus d'XP chaque semaine pour remporter 400 XP !",
      you: "toi",
      xpShort: "XP",
      moodMatrixTitle: "Année en humeurs",
      moodMatrixDesc: "Visualisez votre humeur sur toute l'année",
      moodMatrixNoEntry: "Aucune entrée de journal pour ce jour",
      moodMatrixOpenEntry: "Ouvrir l'entrée complète",
      moodMatrixTracked: "{{count}} humeurs suivies en {{year}}",
      previousYear: "Année précédente",
      nextYear: "Année suivante",
      tutorialAppTutorial: "Tutoriel de l'app",
      tutorialAppTutorialDesc: "Faites une visite rapide de l'application",
      tutorialChooseLanguage: "Choisissez votre langue",
      tutorialChooseLanguageDesc: "Vous pourrez la changer à tout moment dans les paramètres.",
      tutorialContinue: "Continuer",
      tutorialBack: "Retour",
      tutorialNext: "Suivant",
      tutorialSkip: "Passer",
      tutorialGetStarted: "Commencer",
      tutorialClose: "Fermer le tutoriel",
      tutorialStep1Title: "Accueil de votre journal",
      tutorialStep1Desc: "Voici votre écran d'accueil quotidien. Capturez un moment mémorable de la journée, puis appuyez sur Écrire dans le journal pour raconter toute l'histoire. Utilisez la barre du bas pour naviguer entre Journal, Habitudes, Suivis, Amis et Paramètres.",
      tutorialStep2Title: "Écrire une entrée",
      tutorialStep2Desc: "Versez vos pensées dans une entrée complète. Utilisez le microphone pour la saisie vocale, dessinez votre journée avec le pinceau, ou ajoutez une photo pour capturer le moment. Tout est sauvegardé automatiquement.",
      tutorialStep3Title: "Suivez vos habitudes",
      tutorialStep3Desc: "Ajoutez les habitudes que vous voulez construire et cochez-les chaque jour. Restez constant pour gagner de l'XP, monter de niveau et voir vos séries grandir avec le temps.",
      tutorialStep4Title: "Suivis quotidiens",
      tutorialStep4Desc: "Enregistrez votre sommeil, humeur et poids chaque jour. Vos données créent de belles tendances et un calendrier d'humeur pour voir les motifs du mois.",
      tutorialStep5Title: "Amis",
      tutorialStep5Desc: "Connectez-vous avec des amis par nom d'utilisateur ou e-mail pour voir vos progrès d'habitudes mutuels. Envoyez et acceptez des demandes pour rester motivés ensemble.",
      tutorialStep6Title: "Tournoi hebdomadaire",
      tutorialStep6Desc: "Affrontez vos amis chaque semaine. Gagnez le plus d'XP du journal, des habitudes et des suivis pour remporter le tournoi et un bonus d'XP !",
      resetPassword: "Réinitialiser le mot de passe",
      resetPasswordSubtitle: "Choisissez un nouveau mot de passe pour votre compte.",
      newPassword: "Nouveau mot de passe",
      confirmNewPassword: "Confirmer le mot de passe",
      updatePassword: "Mettre à jour le mot de passe",
      passwordUpdated: "Mot de passe mis à jour. Redirection…",
      resetLinkExpired: "Ce lien est invalide ou a expiré.",
      requestNewResetLink: "Demander un nouveau lien",
      passwordsDoNotMatch: "Les mots de passe ne correspondent pas",
      passwordMinLength: "Le mot de passe doit contenir au moins 8 caractères",
      passwordNeedsUppercase: "Le mot de passe doit contenir au moins une majuscule",
      passwordNeedsNumber: "Le mot de passe doit contenir au moins un chiffre",
      passwordMustBeNew: "Choisissez un mot de passe que vous n'avez pas encore utilisé.",
      passwordUpdateFailed: "Impossible de mettre à jour le mot de passe. Réessayez.",
      searchPlaceholder: "Rechercher moments, entrées et croquis",
      globalSearch: "Rechercher dans le journal",
      searchMoment: "Moment",
      searchEntry: "Entrée",
      searchSketch: "Croquis",
      exportData: "Exporter",
      exportMarkdown: "Télécharger Markdown",
      exportJson: "Télécharger JSON",
      exportDesc: "Enregistrez une copie de votre journal, habitudes et suivis.",
      exporting: "Préparation du téléchargement…",
      journalReminder: "Rappel du journal",
      journalReminderDesc: "Un rappel si vous n'avez pas encore écrit.",
      journalReminderTime: "Heure du rappel",
      enableReminder: "Activer le rappel",
      notificationsBlocked: "Les notifications sont bloquées dans ce navigateur.",
      writeToday: "Écrire l'entrée d'aujourd'hui",
    }
  },
  ar: {
    translation: {
      settings: "الإعدادات",
      appearance: "المظهر",
      theme: "المظهر",
      darkMode: "الوضع الداكن",
      lightMode: "الوضع الفاتح",
      language: "اللغة",
      navigation: "التنقل",
      dashboard: "لوحة التحكم",
      openDashboard: "فتح لوحة التحكم",
      dashboardDesc: "إحصائيات ونظرة عامة للشهر",
      insightsTrends: "التحليلات والاتجاهات",
      insightsDesc: "عرض التحليلات والتقدم",
      account: "الحساب",
      signOut: "تسجيل الخروج",
      installApp: "تحميل التطبيق",
      installAppDesc: "ثبّت Life Journal على جهازك للوصول السريع",
      installAppIOS: "اضغط زر المشاركة ثم \"إضافة إلى الشاشة الرئيسية\"",
      quickAccess: "وصول سريع لإحصائياتك.",
      journal: "يوميات",
      name: "الاسم",
      favouriteMoments: "اللحظات المفضلة",
      sketchGallery: "معرض الرسومات",
      sketchGallerySubtitle: "جميع رسوماتك في مكان واحد.",
      allJournalEntries: "جميع إدخالات اليوميات",
      allJournalEntriesSubtitle: "تصفح وبحث في جميع إدخالات يومياتك.",
      allJournalEntriesCount: "{{shown}} من {{total}} إدخال",
      noJournalEntriesYet: "لا توجد إدخالات في اليوميات بعد.",
      noJournalEntriesHint: "اكتب في يومياتك لرؤية الإدخالات هنا.",
      search: "بحث",
      searchByDate: "البحث بالتاريخ...",
      searchSketches: "البحث في الرسومات",
      noSketchesYet: "لا توجد رسومات بعد.",
      noSketchesHint: "ارسم يومك من اليوميات لرؤيته هنا.",
      noMatchesFor: "لا توجد نتائج لـ \"{{query}}\"",
      sketchFrom: "رسم من {{date}}",
      viewEntry: "عرض الإدخال",
      sketchCount: "{{count}} من {{total}} رسم",
      units: "الوحدات",
      weight: "الوزن",
      totalXP: "إجمالي النقاط",
      level: "المستوى",
      levelUp: "ترقية المستوى",
      editJournalSettings: "تحرير إعدادات اليوميات",
      editJournalName: "تحرير اسم اليوميات",
      renameJournal: "إعادة تسمية اليوميات",
      enterJournalName: "أدخل اسم اليوميات",
      journalNameCannotBeEmpty: "اسم اليوميات لا يمكن أن يكون فارغاً",
      characters: "حرف",
      cancel: "إلغاء",
      save: "حفظ",
      journalTab: "يوميات",
      journalAndHabits: "اليوميات والعادات",
      habitsTab: "العادات",
      trackersTab: "المتتبعات",
      friendsTab: "الأصدقاء",
      settingsTab: "الإعدادات",
      back: "رجوع",
      loading: "جاري التحميل...",
      verifyEmail: "تأكيد بريدك الإلكتروني",
      verifySubtitle: "أرسلنا رابط تأكيد إلى",
      verifyCheck: "راجع صندوق الوارد وانقر على الرابط للمتابعة.",
      verifiedBtn: "لقد أكدت بريدي الإلكتروني",
      signOutDifferent: "تسجيل الخروج واستخدام حساب آخر",
      photo: "صورة",
      addPhoto: "إضافة صورة",
      removePhoto: "إزالة الصورة",
      addPhotoTypes: "إضافة صورة (JPEG أو PNG أو WebP)",
      sketchPad: "لوحة الرسم",
      writeJournal: "كتابة اليوميات",
      tellStory: "احكِ قصتك",
      sketchDay: "ارسم يومك",
      doodleThoughts: "ارسم أفكارك",
      captureMoment: "التقط اللحظة",
      memorableMoments: "اللحظات الباقية",
      whatMadeSpecial: "ما الذي جعل هذا اليوم مميزاً؟",
      journalEntry: "إدخال اليوميات",
      yourThoughts: "أفكارك...",
      pourThoughtsPlaceholder: "اسكب أفكارك هنا...",
      addNewHabitPlaceholder: "إضافة عادة جديدة...",
      todaysProgressLabel: "تقدم اليوم",
      memorableMoment: "لحظة تبقى في الذاكرة...",
      statsFor: "إحصائيات {{month}} {{year}}",
      journalEntries: "إدخالات اليوميات",
      ofDays: "من {{n}} يوم",
      memorableMomentsCount: "اللحظات الباقية",
      habitsCompleted: "العادات المكتملة",
      ofGoal: "{{pct}}% من الهدف",
      noHabitsSet: "لم تُضبط عادات",
      xpEarnedThisMonth: "النقاط المكتسبة هذا الشهر",
      consistency: "الاتساق",
      journalStreak: "سلسلة اليوميات",
      xpEarned: "النقاط المكتسبة",
      moodAvg: "متوسط المزاج",
      levelInLevel: "مستوى {{level}} · {{current}}/{{total}} في المستوى",
      last7DaysTrend: "اتجاه آخر 7 أيام",
      levelTotal: "المستوى {{level}} إجمالاً",
      avgSleep: "متوسط النوم",
      daysLogged: "{{n}} يوم مُسجل",
      noData: "لا توجد بيانات",
      logsCount: "{{n}} تسجيل",
      moodDistribution: "توزيع المزاج",
      noMoodData: "لا توجد بيانات مزاج لهذا الشهر",
      noMoodSet: "لم يُضبط مزاج",
      sleepOverMonth: "النوم خلال الشهر (ساعات)",
      eightHourGoal: "— الخط الأخضر المتقطع = هدف 8 ساعات",
      noSleepData: "لا توجد بيانات نوم لهذا الشهر",
      weightTrend: "اتجاه الوزن ({{unit}})",
      habitCompletions: "إكمالات العادات هذا الشهر",
      habitCompletionsDesc: "مرات إكمال كل عادة في {{month}} {{year}}",
      moodBreakdown: "تفصيل المزاج",
      dayLabel: "اليوم {{n}}",
      moodTooltip: "المزاج",
      mood: "المزاج",
      sleepTooltip: "النوم",
      weightTooltip: "الوزن",
      moodCalendar: "تقويم المزاج",
      tapToSet: "انقر للتعيين",
      tapDayCycleMood: "انقر على يوم لتغيير المزاج",
      noTrackerData: "لا بيانات متتبعة لهذا الشهر بعد. ابدأ التسجيل أعلاه!",
      sleepHoursLabel: "النوم (ساعات)",
      saved: "محفوظ",
      uploading: "جاري الرفع...",
      hours: "ساعات",
      hrs: "ساعات",
      moodEcstatic: "مبتهج",
      moodHappy: "سعيد",
      moodNeutral: "محايد",
      moodSad: "حزين",
      moodAngry: "غاضب",
      monthJanuary: "يناير",
      monthFebruary: "فبراير",
      monthMarch: "مارس",
      monthApril: "أبريل",
      monthMay: "مايو",
      monthJune: "يونيو",
      monthJuly: "يوليو",
      monthAugust: "أغسطس",
      monthSeptember: "سبتمبر",
      monthOctober: "أكتوبر",
      monthNovember: "نوفمبر",
      monthDecember: "ديسمبر",
      monthShortJan: "يناير",
      monthShortFeb: "فبراير",
      monthShortMar: "مارس",
      monthShortApr: "أبريل",
      monthShortMay: "مايو",
      monthShortJun: "يونيو",
      monthShortJul: "يوليو",
      monthShortAug: "أغسطس",
      monthShortSep: "سبتمبر",
      monthShortOct: "أكتوبر",
      monthShortNov: "نوفمبر",
      monthShortDec: "ديسمبر",
      weekdaySun: "الأحد",
      weekdayMon: "الإثنين",
      weekdayTue: "الثلاثاء",
      weekdayWed: "الأربعاء",
      weekdayThu: "الخميس",
      weekdayFri: "الجمعة",
      weekdaySat: "السبت",
      dailyLog: "السجل اليومي",
      addHabitPlaceholder: "إضافة عادة...",
      memorableMomentHeader: "لحظة تبقى في الذاكرة",
      oneLineMemorableMoment: "لحظة تبقى في الذاكرة في سطر واحد...",
      sleepTracker: "متابعة النوم",
      moodTracker: "متابعة المزاج",
      weightTracker: "متابعة الوزن",
      sleepForMonth: "النوم لـ {{month}} {{year}}",
      moodForMonth: "المزاج لـ {{month}} {{year}}",
      weightForMonth: "الوزن لـ {{month}} {{year}}",
      hrsAvg: "ساعات متوسط",
      overall: "الإجمالي",
      last: "آخر",
      sleepHrsHeader: "النوم (ساعات)",
      weightHeaderUnit: "الوزن ({{unit}})",
      enterSleepHours: "أدخل ساعات النوم في الجدول أدناه",
      habitTracker: "متابعة العادات",
      trackersHome: "المتابعات",
      habit: "عادة",
      clickDayCycleMood: "انقر على يوم لتغيير المزاج",
      timeline: "الجدول الزمني",
      insightsSubtitle: "تتبع اتساقك وراقب تقدمك على المدى الطويل.",
      previousMonth: "الشهر السابق",
      nextMonth: "الشهر التالي",
      consistencyScore: "درجة الاتساق",
      up: "أعلى",
      sleep: "النوم",
      sleepThisMonth: "هذا الشهر (ساعات في الليلة)",
      weightThisMonth: "هذا الشهر ({{unit}})",
      average: "المتوسط",
      min: "الحد الأدنى",
      max: "الحد الأقصى",
      emptySleep: "لا بيانات نوم لهذا الشهر بعد",
      emptyWeight: "لا بيانات وزن لهذا الشهر بعد",
      changeThisMonth: "التغيير هذا الشهر",
      moodCorrelation: "ارتباط مزاج اليوميات",
      moodCorrelationSub: "تأثير عاداتك اليومية على صحتك العاطفية",
      daySingle: "يوم",
      daysPlural: "أيام",
      emptyMoodInsights: "لا بيانات مزاج لهذا الشهر",
      insightEngine: "محرك الرؤى",
      insightText: "مزاجك الأكثر تكراراً هذا الشهر هو \"{{mood}}\". استمر بالتتبع لاكتشاف أنماط.",
      streaks: "السلاسل",
      daysJournaled: "أيام الكتابة",
      current: "الحالي",
      longest: "الأطول",
      emptyStreaks: "أضف عادات لتتبع المزيد من السلاسل",
      monthLogs: "سجلات الشهر",
      page: "صفحة",
      pageOf: "صفحة {{current}} من {{total}}",
      date: "التاريخ",
      habitsDone: "العادات المنجزة",
      previousPage: "الصفحة السابقة",
      nextPage: "الصفحة التالية",
      days: "أيام",
      voiceInputStart: "بدء الإدخال الصوتي",
      voiceInputStop: "إيقاف الإدخال الصوتي",
      voiceInputUnsupported: "الإدخال الصوتي غير مدعوم",
      voiceInputListening: "جاري الاستماع...",
      tooltipClickDayWrite: "👆 انقر على أي يوم لكتابة إدخال كامل!",
      tooltipClose: "إغلاق التلميح",
      sleepChartHint: "نصيحة: انقر لإضافة نقاط، انقر على النقاط الموجودة لتعديلها",
      daysLoggedLabel: "الأيام المسجلة",
      change: "التغيير",
      entries: "الإدخالات",
      setWeightGoal: "هدف الوزن ({{unit}})",
      enterGoalKg: "الهدف (كغم)",
      enterGoalLbs: "الهدف (رطل)",
      clearGoal: "مسح الهدف",
      addYearTitle: "إضافة سنة",
      enterYearPlaceholder: "السنة (مثلاً 2026)",
      pleaseEnterYear: "يرجى إدخال سنة",
      pleaseEnterValidYear: "أدخل سنة صالحة (1900-2100)",
      yearAlreadyExists: "هذه السنة موجودة مسبقاً",
      monthLabel: "الشهر:",
      deleteYearsTitle: "حذف السنوات",
      deleteYearsWarning: "أنت على وشك حذف {{count}} سنة: {{years}}",
      deleteYearsCannotUndo: "لا يمكن التراجع عن هذا الإجراء. ستُفقد جميع الإدخالات والعادات والبيانات لهذه السنوات نهائياً.",
      typeToConfirm: "اكتب \"i understand\" للتأكيد:",
      deleteYearsPlaceholder: "i understand",
      deleting: "جاري الحذف…",
      deleteYearsBtn: "حذف السنوات",
      removeHabitTitle: "إزالة العادة؟",
      removeHabitConfirmMobile: "هل أنت متأكد أنك تريد إزالة {{habit}}؟ ستفقد كل سجل الإكمال.",
      removeHabitConfirmWeb: "هل أنت متأكد أنك تريد إزالة {{habit}}؟ سيتم حذف العادة وتاريخها من كل الأيام.",
      setReminder: "تعيين تذكير",
      changeReminder: "تغيير التذكير",
      removeReminder: "إزالة التذكير",
      hideFromFriends: "إخفاء عن الأصدقاء",
      hideFromFriendsSubtitle: "أنت فقط يمكنك رؤية هذا",
      habitCardColor: "لون البطاقة",
      habitCardColorDefault: "افتراضي",
      habitCardColorCustom: "مخصص",
      setReminderTitle: "تذكير يومي",
      setReminderDesc: "احصل على إشعار لـ {{habit}} في:",
      deleteHabitTitle: "حذف العادة؟",
      dontWarnAgain: "عدم التحذير مرة أخرى",
      deselectForDeletion: "إلغاء التحديد للحذف",
      selectForDeletion: "تحديد للحذف",
      selectYear: "تحديد {{y}}",
      addYear: "إضافة سنة",
      removeYears: "إزالة السنوات",
      confirmDeleteYears: "تأكيد حذف السنوات المحددة",
      deleteWithCount: "حذف ({{count}})",
      friends: "الأصدقاء",
      menu: "القائمة",
      openMenu: "فتح القائمة",
      add: "إضافة",
      previousDay: "اليوم السابق",
      nextDay: "اليوم التالي",
      clear: "مسح",
      unfavourite: "إزالة من المفضلة",
      favourite: "مفضل",
      unfavouriteThisDay: "إزالة هذا اليوم من المفضلة",
      favouriteThisDay: "إضافة هذا اليوم للمفضلة",
      viewFullSizePhoto: "عرض الصورة بحجم كامل",
      uploadPhoto: "رفع صورة",
      closeFullscreenImage: "إغلاق الصورة ملء الشاشة",
      close: "إغلاق",
      selectColor: "تحديد اللون {{color}}",
      customColor: "لون مخصص",
      // وسوم الموقع وخريطة اليوميات
      location: "الموقع",
      addLocation: "إضافة موقع",
      changeLocation: "تغيير الموقع",
      removeLocation: "إزالة الموقع",
      searchLocation: "ابحث عن مدينة أو مكان…",
      useMyLocation: "استخدام موقعي الحالي",
      locating: "جارٍ تحديد الموقع…",
      locationOffline: "يتطلب البحث عن الأماكن اتصالاً بالإنترنت.",
      locationSearchFailed: "تعذر العثور على هذا المكان. حاول مجدداً.",
      locationPermissionDenied: "تم رفض الوصول إلى الموقع. لا يزال بإمكانك البحث عن مكان.",
      noLocationResults: "لم يتم العثور على أماكن.",
      journalMap: "خريطة اليوميات",
      journalMapDesc: "شاهد كل البلدان التي كتبت فيها.",
      journalMapSummary: "كتبت في {{countries}} بلداً · {{entries}} تدوينة موسومة بموقع",
      showWholeWorld: "العالم كله",
      entryCount: "{{count}} تدوينة",
      countriesVisited: "{{count}} بلد",
      selectCountryHint: "اضغط على بلد مميز أو علامة لرؤية ما كتبته هناك.",
      noLocationsYet: "لا توجد أماكن بعد",
      noLocationsYetDesc: "أضف موقعاً إلى تدوينة وسيظهر على هذه الخريطة.",
      noTextForDay: "لا يوجد نص لهذا اليوم",
      eraser: "ممحاة",
      undo: "تراجع",
      switchToWhiteBg: "التبديل لخلفية بيضاء",
      switchToDarkBg: "التبديل لخلفية داكنة",
      levelAndXP: "المستوى والنقاط",
      moreOptions: "المزيد من الخيارات",
      notCompletedYet: "لم يكتمل بعد",
      searchFavouriteEntries: "البحث في الإدخالات المفضلة",
      clearSearch: "مسح البحث",
      favouriteThisMoment: "إضافة هذه اللحظة للمفضلة",
      markHabitIncomplete: "تحديد {{habit}} كناقص",
      markHabitComplete: "تحديد {{habit}} كمكتمل",
      levelTooltip: "المستوى {{level}}",
      toggleTheme: "تبديل المظهر",
      journalStreakTooltip: "سلسلة الكتابة – أيام متتالية كتبت فيها",
      weightUnitLabel: "وحدة الوزن",
      today: "اليوم",
      done: "تم",
      backToJournal: "العودة إلى اليوميات",
      friendsSetupRequired: "إعداد قاعدة البيانات مطلوب",
      friendsSetupRun: "نفّذ FRIENDS_SETUP.sql في محرر SQL في Supabase لتفعيل الأصدقاء.",
      displayName: "الاسم المعروض",
      editProfile: "تحرير الملف الشخصي",
      experience: "الخبرة",
      requests: "الطلبات",
      findFriends: "البحث عن أصدقاء",
      loadingFriends: "جاري تحميل الأصدقاء...",
      noFriendsYet: "لا أصدقاء بعد",
      friendsSearchHint: "ابحث عن أصدقاء باسم المستخدم أو البريد للاتصال ورؤية عاداتهم!",
      removeFriend: "إزالة صديق",
      completedAt: "مكتمل في {{time}}",
      todaysProgress: "تقدم اليوم:",
      noHabitsTracked: "لا عادات مُتتبعة بعد",
      receivedRequests: "الطلبات الواردة",
      sentRequests: "الطلبات المرسلة",
      noPendingRequests: "لا طلبات معلقة",
      accept: "قبول",
      decline: "رفض",
      searchFriendsPlaceholder: "البحث باسم المستخدم أو البريد...",
      searching: "جاري البحث...",
      searchMinChars: "اكتب حرفين على الأقل للبحث",
      noUsersFound: "لم يُعثر على مستخدمين يطابقون \"{{query}}\"",
      friendsAlready: "أصدقاء ✓",
      pending: "قيد الانتظار",
      addFriend: "إضافة صديق",
      friendsInviteFooter: "ادعُ أصدقاء لربح نقاط إضافية والبقاء محفزاً!",
      removeFriendTitle: "إزالة الصديق؟",
      removeFriendConfirm: "إزالة {{name}} من قائمة أصدقائك؟ سيحتاج لإرسال طلب جديد للاتصال مرة أخرى.",
      remove: "إزالة",
      setYourName: "اضبط اسمك",
      usernameLabel: "اسم المستخدم",
      usernamePlaceholder: "اسم المستخدم",
      usernameEmpty: "اسم المستخدم لا يمكن أن يكون فارغاً",
      usernameTaken: "اسم المستخدم مُستخدم بالفعل",
      profileUpdateFailed: "فشل تحديث الملف الشخصي. حاول مرة أخرى.",
      friendRequestFailed: "فشل إرسال طلب الصداقة. حاول مرة أخرى.",
      syncing: "جاري المزامنة...",
      offline: "غير متصل",
      leaderboard: "الترتيب",
      weeklyLeaderboard: "منافسة أسبوعية",
      leaderboardWinnerReward: "🏆 الفائز يحصل على {{xp}} XP!",
      leaderboardResetsIn: "يعيد الضبط خلال {{days}}يوم {{hours}}س {{mins}}د",
      leaderboardNoData: "أكمل العادات لربح XP والصعود في الترتيب!",
      leaderboardNeedsFriends: "أضف صديقاً للمنافسة!",
      leaderboardInviteFriend: "ادعُ صديقاً للمنافسة الأسبوعية. اربح أكبر قدر من XP كل أسبوع للفوز بـ 400 XP!",
      you: "أنت",
      xpShort: "XP",
      moodMatrixTitle: "السنة في الأمزجة",
      moodMatrixDesc: "شاهد مزاجك على مدار السنة",
      moodMatrixNoEntry: "لا توجد إدخالات يومية لهذا اليوم",
      moodMatrixOpenEntry: "فتح الإدخال الكامل",
      moodMatrixTracked: "{{count}} مزاج تم تتبعه في {{year}}",
      previousYear: "السنة السابقة",
      nextYear: "السنة التالية",
      tutorialAppTutorial: "جولة التطبيق",
      tutorialAppTutorialDesc: "خذ جولة سريعة في التطبيق",
      tutorialChooseLanguage: "اختر لغتك",
      tutorialChooseLanguageDesc: "يمكنك تغييرها في أي وقت من الإعدادات.",
      tutorialContinue: "متابعة",
      tutorialBack: "رجوع",
      tutorialNext: "التالي",
      tutorialSkip: "تخطّي",
      tutorialGetStarted: "ابدأ",
      tutorialClose: "إغلاق الجولة",
      tutorialStep1Title: "الصفحة الرئيسية ليومياتك",
      tutorialStep1Desc: "هذه شاشتك الرئيسية اليومية. التقط لحظة مميزة من اليوم، ثم اضغط على \"اكتب اليوميات\" لتروي القصة كاملة. استخدم الشريط السفلي للتنقل بين اليوميات والعادات والمتعقبات والأصدقاء والإعدادات.",
      tutorialStep2Title: "اكتب تدوينة يومية",
      tutorialStep2Desc: "اسكب أفكارك في تدوينة كاملة. استخدم الميكروفون للإدخال الصوتي، أو ارسم يومك بالفرشاة، أو أرفق صورة لتلتقط اللحظة. كل شيء يُحفظ تلقائياً أثناء الكتابة.",
      tutorialStep3Title: "تتبع عاداتك",
      tutorialStep3Desc: "أضف العادات التي تريد بناءها وضع علامة عليها كل يوم. حافظ على الانتظام لكسب نقاط الخبرة، وارتقِ بالمستوى، وشاهد سلسلتك تنمو مع الوقت.",
      tutorialStep4Title: "متعقّبات يومية",
      tutorialStep4Desc: "سجّل نومك ومزاجك ووزنك كل يوم. تبني بياناتك اتجاهات جميلة وتقويم مزاج لترى الأنماط على مدار الشهر.",
      tutorialStep5Title: "الأصدقاء",
      tutorialStep5Desc: "تواصل مع الأصدقاء عبر اسم المستخدم أو البريد الإلكتروني لرؤية تقدّم عادات بعضكم البعض. أرسل واقبل طلبات الصداقة لتبقوا متحفّزين معاً.",
      tutorialStep6Title: "البطولة الأسبوعية",
      tutorialStep6Desc: "تنافس مع أصدقائك كل أسبوع. اكسب أكبر قدر من نقاط الخبرة من اليوميات والعادات والمتعقبات لتفوز بالبطولة ومكافأة إضافية!",
      resetPassword: "إعادة تعيين كلمة المرور",
      resetPasswordSubtitle: "اختر كلمة مرور جديدة لحسابك.",
      newPassword: "كلمة المرور الجديدة",
      confirmNewPassword: "تأكيد كلمة المرور",
      updatePassword: "تحديث كلمة المرور",
      passwordUpdated: "تم تحديث كلمة المرور. جارٍ التحويل…",
      resetLinkExpired: "رابط إعادة التعيين غير صالح أو منتهٍ.",
      requestNewResetLink: "طلب رابط جديد",
      passwordsDoNotMatch: "كلمتا المرور غير متطابقتين",
      passwordMinLength: "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل",
      passwordNeedsUppercase: "يجب أن تحتوي كلمة المرور على حرف كبير واحد على الأقل",
      passwordNeedsNumber: "يجب أن تحتوي كلمة المرور على رقم واحد على الأقل",
      passwordMustBeNew: "اختر كلمة مرور لم تستخدمها من قبل.",
      passwordUpdateFailed: "تعذر تحديث كلمة المرور. حاول مرة أخرى.",
      searchPlaceholder: "ابحث في اللحظات والإدخالات والرسوم",
      globalSearch: "البحث في اليوميات",
      searchMoment: "لحظة",
      searchEntry: "إدخال",
      searchSketch: "رسم",
      exportData: "تصدير",
      exportMarkdown: "تنزيل Markdown",
      exportJson: "تنزيل JSON",
      exportDesc: "احفظ نسخة من يومياتك وعاداتك والمتتبعات.",
      exporting: "جارٍ تحضير التنزيل…",
      journalReminder: "تذكير اليوميات",
      journalReminderDesc: "تنبيه إذا لم تكتب بعد اليوم.",
      journalReminderTime: "وقت التذكير",
      enableReminder: "تفعيل التذكير",
      notificationsBlocked: "الإشعارات محظورة في هذا المتصفح.",
      writeToday: "اكتب إدخال اليوم",
    }
  },
};

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar', 'es', 'fr'],
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    interpolation: {
      escapeValue: false,
    },
  });

// RTL for Arabic
i18n.on('languageChanged', (lng) => {
  document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = lng || 'en';
});
if (typeof document !== 'undefined' && document.documentElement) {
  const lang = i18n.language?.split('-')[0];
  if (lang === 'ar') {
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'ar';
  }
}

export { LANGUAGES };
export default i18n;
